import { RangeValue } from '@react-types/shared';
import { useCallback, useMemo } from 'react';
import { DateValue } from 'react-aria';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import { FilterTagsCondition } from 'dashboard/components/TagsConditionFilter';
import { FormCreateAccountData } from 'dashboard/forms/FormCreateAccount';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { Account } from 'dashboard/models/account';
import { ExporterFileType } from 'dashboard/models/exporter';
import { ProductFeatureName } from 'dashboard/models/organization';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  saveAccountsFilters,
  saveAccountsSortParams,
  saveCurrentPage,
  savePageSize,
} from 'dashboard/redux/slices/accounts';
import { RootState } from 'dashboard/redux/store';
import { loadAccountStatuses } from 'dashboard/redux/thunk-actions/account-statuses';
import { loadAccountTypesFilterOptions } from 'dashboard/redux/thunk-actions/account-types';
import { loadAccountsList, loadExporterAccount } from 'dashboard/redux/thunk-actions/accounts';
import { loadLists } from 'dashboard/redux/thunk-actions/list';
import { loadTags } from 'dashboard/redux/thunk-actions/tags';
import DashboardAccountsView from 'dashboard/views/DashboardAccountsView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import usePermission from 'lib/hooks/usePermission';
import useTable from 'lib/hooks/useTable';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { Iso8601DateString } from 'lib/types';
import { updateDateUpdatedFilters } from 'lib/utils/filters';

const DashboardAccountsRoute = (props: PropsFromRedux) => {
  const {
    currentEnvironment,
    currentPage,
    pageSize,
    accountsFilters,
    accountsSortParams,
    organization,
    featureFlags,
  } = props;
  const navigate = useNavigate();
  const productFeatureIsDisabled = productFeatureDisabled(
    organization,
    ProductFeatureName.AccountsDashboard,
    currentEnvironment.type,
  );

  const exportersProductFeatureIsDisabled = productFeatureDisabled(
    organization,
    ProductFeatureName.Exporters,
    currentEnvironment.type,
  );

  const { handlePageLinkClick, handlePageSizeClick, handleOmniSearch, handleSortUpdate } = useTable(
    {
      filters: accountsFilters,
      sortParams: accountsSortParams,
      pageSize: props.pageSize,
      currentPage: props.currentPage,
      setFilters: props.saveAccountsFilters,
      setSortParams: props.saveAccountsSortParams,
      setCurrentPage: props.saveCurrentPage,
      setPageSize: props.savePageSize,
    },
  );

  // Required to abort requests while props are changing quickly
  const abortController = useMemo(
    () => new AbortController(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.loadAccountsList,
      props.currentEnvironment,
      props.currentPage,
      props.pageSize,
      accountsSortParams,
      accountsFilters,
    ],
  );

  const { loading: isLoadingAccounts, callback: loadAccounts } = useApiLoader({
    loader: () =>
      props.loadAccountsList({
        currentEnvironment,
        currentPage,
        pageSize,
        sortParams: accountsSortParams,
        filters: accountsFilters,
        abortSignal: abortController.signal,
      }),
    deps: [
      props.loadAccountsList,
      currentEnvironment,
      currentPage,
      pageSize,
      accountsFilters,
      accountsSortParams,
    ],
    skip: productFeatureIsDisabled,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load accounts.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: () => props.loadTags(ApiModel.Account),
    deps: [props.loadTags],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load tags.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: () => props.loadAccountStatuses(),
    deps: [props.loadAccountStatuses],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load account statuses.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: () => props.loadAccountTypesFilterOptions(),
    deps: [props.loadAccountTypesFilterOptions],
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    onError: null,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    onNetworkError: null,
  });

  const handleExportData = async (fileType: ExporterFileType) => {
    try {
      await props.loadExporterAccount({
        currentEnvironment,
        filters: accountsFilters,
        sortParams: accountsSortParams,
        fileType: fileType,
      });
      props.showMessage('You will receive an email when your export is ready.', MessageType.Notice);
    } catch {
      props.showMessage('Failed to create export, please try again.', MessageType.Failure);
    }
  };

  const handleDateUpdate = useCallback(
    (
      newDateRange: {
        updatedAtEnd: Iso8601DateString | undefined;
        updatedAtStart: Iso8601DateString | undefined;
      } | null,
    ) => {
      if (newDateRange) {
        const { updatedAtStart, updatedAtEnd } = newDateRange;
        if (updatedAtStart && updatedAtEnd) {
          props.saveAccountsFilters({
            ...accountsFilters,
            updatedAtStart: updatedAtStart,
            updatedAtEnd: updatedAtEnd,
          });
        } else {
          props.saveAccountsFilters({
            ...accountsFilters,
            updatedAtStart: undefined,
            updatedAtEnd: undefined,
          });
        }
      }
    },
    [props.saveAccountsFilters, accountsFilters],
  );

  const handleDateUpdateV2 = useCallback(
    (newDateRange: RangeValue<DateValue> | null) => {
      updateDateUpdatedFilters(newDateRange, handleDateUpdate);
    },
    [props.saveAccountsFilters, accountsFilters],
  );

  const hasAccountViewPermission = usePermission(Permission.AccountView);

  const { error: handleCreateAccountError, callback: handleCreateAccount } = useApiCallback(
    async ({
      referenceId,
      accountTypeId,
      nameFirst,
      nameMiddle,
      nameLast,
    }: FormCreateAccountData) => {
      const payload = {
        attributes: {
          'reference-id': referenceId,
          'environment-id': props.currentEnvironment.id,
          'account-type-id': accountTypeId,
          'name-first': nameFirst,
          'name-middle': nameMiddle,
          'name-last': nameLast,
        },
        type: ApiModel.Account as const,
      };

      const response = await SessionClientDashboard.postResourceCreate<Account>(payload);

      props.showMessage('Successfully created account.', MessageType.Success);

      if (hasAccountViewPermission) {
        navigate(`/dashboard/accounts/${response.data.id}`);
      }
    },
    [loadAccounts, hasAccountViewPermission],
  );

  useApiErrorHandler({
    error: handleCreateAccountError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (error.status === 409) {
        props.showMessage('Account already exists with this reference ID', MessageType.Failure);
      } else {
        props.showMessage('Failed to create account', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: () =>
      props.loadLists({
        currentEnvironment,
        filters: {},
        sortParams: { sort: '-', shouldSortDescending: false },
      }),
    deps: [currentEnvironment],
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage('Failed to load lists.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  const handleTagFilterUpdate = (tagIds: string[]) => {
    props.saveAccountsFilters({
      tagIds: tagIds,
    });
    props.saveCurrentPage(1);
  };

  const handleTagsConditionsFilterUpdate = (tagsConditions: FilterTagsCondition[][]) => {
    props.saveAccountsFilters({ tagsConditions: tagsConditions });
    props.saveCurrentPage(1);
  };

  const handleAccountStatusFilterUpdate = (accountStatusIds: string[]) => {
    props.saveAccountsFilters({
      accountStatusIds: accountStatusIds,
    });
    props.saveCurrentPage(1);
  };

  const handleAccountTypeFilterUpdate = (accountTypeIds: string[]) => {
    props.saveAccountsFilters({
      accountTypeIds: accountTypeIds,
    });
    props.saveCurrentPage(1);
  };

  const handleListFilterUpdate = (listIds: string[]) => {
    props.saveAccountsFilters({ listIds });
    props.saveCurrentPage(1);
  };

  return (
    <DashboardAccountsView
      accounts={props.accounts}
      accountStatuses={props.accountStatuses}
      accountTypesFilterOptions={props.accountTypesFilterOptions}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      accountPaginationParams={props.accountPaginationParams}
      tags={props.tags}
      pageSize={pageSize}
      accountsFilters={accountsFilters}
      sortParams={accountsSortParams}
      isLoadingAccounts={isLoadingAccounts}
      currentEnvironment={currentEnvironment}
      featureFlags={featureFlags}
      lists={props.lists}
      handleExportData={handleExportData}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      handleDateUpdate={handleDateUpdateV2}
      handleOmniSearch={handleOmniSearch}
      handleSortUpdate={handleSortUpdate}
      handleAccountStatusFilterUpdate={handleAccountStatusFilterUpdate}
      handleAccountTypeFilterUpdate={handleAccountTypeFilterUpdate}
      handleTagFilterUpdate={handleTagFilterUpdate}
      handleTagsConditionsFilterUpdate={handleTagsConditionsFilterUpdate}
      handleCreateAccount={handleCreateAccount}
      handleListFilterUpdate={handleListFilterUpdate}
      productFeatureIsDisabled={productFeatureIsDisabled}
      exportersProductFeatureIsDisabled={exportersProductFeatureIsDisabled}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    accounts: state.accounts.accounts[state.currentEnvironment.currentEnvironment.id] || [],
    accountStatuses: state.accountStatuses.accountStatuses,
    accountTypesFilterOptions: state.accountTypes.filterOptions,
    accountsFilters: state.accounts.filters,
    accountsSortParams: state.accounts.sortParams,
    accountPaginationParams: state.accounts.paginationParams,
    pageSize: state.accounts.pageSize,
    environments: state.organization.environments,
    featureFlags: state.featureFlags.featureFlags,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    currentPage: state.accounts.currentPage,
    organization: state.organization.organization,
    tags: state.tags.tags.account,
    lists: state.lists.lists,
  };
};

const mapDispatchToProps = {
  loadAccountsList,
  loadAccountStatuses,
  loadAccountTypesFilterOptions,
  loadTags,
  loadExporterAccount,
  loadLists,
  savePageSize,
  saveCurrentPage,
  showMessage,
  saveAccountsFilters,
  saveAccountsSortParams,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardAccountsRoute);
